import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Branch } from '@retailpro/shared';

import { BrandMark } from '../components/BrandMark';
import { ErrorNotice } from '../components/ErrorNotice';
import { UiToggles } from '../components/UiToggles';
import { useUi } from '../i18n/ui-context';
import { formatNumber } from '../i18n/strings';
import { ApiError } from '../lib/api-client';
import { useSession } from '../session/session-context';

type NavKey = 'dashboard' | 'items' | 'branches' | 'eta';

const NAV: { key: NavKey; label: Parameters<ReturnType<typeof useUi>['t']>[0]; ready: boolean }[] =
  [
    { key: 'dashboard', label: 'nav.dashboard', ready: true },
    { key: 'items', label: 'nav.items', ready: false },
    { key: 'branches', label: 'nav.branches', ready: true },
    { key: 'eta', label: 'nav.eta', ready: false },
  ];

/**
 * Shell plus the one screen that is genuinely wired up end to end.
 *
 * The point of this page is to prove the request path: sign in, carry the access
 * token, render a real API response, and survive the server being unreachable.
 * Modules that are not built yet are listed but disabled rather than hidden, so
 * the navigation does not appear to change shape as features land.
 */
export function DashboardPage() {
  const { t, direction } = useUi();
  const { session, api, signOut } = useSession();
  const navigate = useNavigate();

  const [branches, setBranches] = useState<Branch[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const loadBranches = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const page = await api.branches(session.accessToken);
      setBranches(page.data);
    } catch (cause) {
      setBranches(null);
      setError(cause);
      // An expired or revoked token should land the user on the login screen
      // with a reason, not on a dashboard that silently shows nothing.
      if (cause instanceof ApiError && cause.isUnauthenticated) {
        navigate('/login', { replace: true, state: { from: '/' } });
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [api, session, navigate]);

  useEffect(() => {
    void loadBranches();
  }, [loadBranches]);

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="shell__brand">
          <BrandMark size={36} />
          <span className="shell__title">{t('app.name')}</span>
        </div>

        <div className="shell__header-actions">
          <UiToggles />
          <button type="button" className="button button--ghost" onClick={() => void signOut()}>
            {t('login.logout')}
          </button>
        </div>
      </header>

      <div className="shell__body">
        <nav className="shell__nav" aria-label={t('app.name')}>
          <ul>
            {NAV.map((entry) => (
              <li key={entry.key}>
                <button
                  type="button"
                  className="nav-item"
                  aria-current={entry.key === 'dashboard' ? 'page' : undefined}
                  disabled={!entry.ready}
                >
                  <span>{t(entry.label)}</span>
                  {!entry.ready && <span className="nav-item__badge">{t('nav.comingSoon')}</span>}
                </button>
              </li>
            ))}
          </ul>

          {session && (
            <p className="shell__identity">
              <span className="shell__identity-label">{t('common.loggedInAs')}</span>
              <strong>{session.user.fullName || session.user.username}</strong>
            </p>
          )}
        </nav>

        <main className="shell__main">
          <h1>{t('nav.dashboard')}</h1>

          <section className="card" aria-labelledby="branches-heading">
            <div className="card__header">
              <h2 id="branches-heading">{t('common.branches')}</h2>
              {loading && <span className="card__status">{t('common.loading')}</span>}
            </div>

            <ErrorNotice error={error} onRetry={() => void loadBranches()} />

            {!loading && !error && branches !== null && branches.length === 0 && (
              <p className="muted">{t('common.noBranches')}</p>
            )}

            {branches !== null && branches.length > 0 && (
              <ul className="list">
                {branches.map((branch) => (
                  <li key={branch.id} className="list__row">
                    <span className="list__code">{branch.code}</span>
                    {/* Both names are always rendered: an Arabic-only branch list
                        is unreadable to the English-speaking staff a shop hires. */}
                    <span className="list__name">{branch.nameAr}</span>
                    {branch.nameEn && (
                      <span className="list__name list__name--alt" lang="en" dir="ltr">
                        {branch.nameEn}
                      </span>
                    )}
                    <span
                      className={branch.isActive ? 'badge badge--ok' : 'badge badge--muted'}
                      dir="ltr"
                    >
                      {branch.isActive ? 'active' : 'inactive'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card" aria-labelledby="stats-heading">
            <div className="card__header">
              <h2 id="stats-heading">{t('nav.overview')}</h2>
            </div>
            <div className="stats">
              <div className="stat">
                <span className="stat__label">{t('common.branches')}</span>
                <span className="stat__value" dir="ltr">
                  {formatNumber('en', branches?.length ?? 0)}
                </span>
              </div>
              <div className="stat">
                <span className="stat__label">{t('nav.items')}</span>
                <span className="stat__value" dir="ltr">
                  —
                </span>
              </div>
              <div className="stat">
                <span className="stat__label">{t('nav.eta')}</span>
                <span className="stat__value" dir="ltr">
                  —
                </span>
              </div>
            </div>
            <p className="muted" dir={direction === 'rtl' ? 'rtl' : 'ltr'}>
              {t('nav.comingSoon')}
            </p>
          </section>
        </main>
      </div>
    </div>
  );
}
